/**
 * Servicio de sincronización de vencimientos fiscales con fuentes externas (ARCA, COMARB).
 * ORR-100 Fase 1+2.
 *
 * Refs:
 *   docs/plan-orr100-vencimientos.md (secciones F1.4, F1.8)
 *   docs/red-team-orr100-vencimientos-2026-05-13.md
 *
 * Diseño:
 * - Server-side: invocado desde Netlify Functions con service-role client.
 * - Read-side (UI admin): invocado desde hooks con cliente authenticated (RLS verifica admin).
 * - Idempotencia: UPSERT contra unique index parcial `vac_unique_per_obligacion WHERE activo = TRUE`.
 * - Defense in depth contra race protected_manual: UPDATEs incluyen `WHERE protected_manual = FALSE`.
 * - Cap del diff: si > 50 → status='partial', NO aplica UPDATEs, alerta admin.
 * - Anti-PII: cliente_responsable_emails calculado pero NO persistido en audit log.
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '../../types/database'
import type {
  SyncSource,
  SyncStatus,
  SyncDiff,
  SyncAuditLog,
  ParsedVencimiento,
  ProcesarParsedResult,
} from '../../types/sync'
import { DIFF_CAP, HISTORICAL_MIN_FETCHED, SUSPICIOUS_FETCH_RATIO } from '../../types/sync'

// TODO post-merge: regenerar src/types/database.ts con las 3 tablas nuevas
// (sync_audit_log, app_settings, sync_rate_limit_log) y la columna nueva protected_manual
// en vencimientos_afip_calendario. Por ahora, helper que castea el client para queries
// contra tablas/columnas pendientes de tipar.
//
// Comando para regenerar (post-merge):
//   npx supabase gen types typescript --linked > src/types/database.ts
//
// Mientras tanto, cast localizado para no contaminar todo el servicio.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseSupabaseClient = SupabaseClient<any, any, any>
function loose(client: SupabaseClient<Database>): LooseSupabaseClient {
  return client as unknown as LooseSupabaseClient
}

// ============================================================================
// Constants
// ============================================================================

const RUNNING_LOCK_WINDOW_MIN = 15  // si hay un sync 'running' < 15 min → no arrancar otro

const NOTIF_DEDUP_HOURS = 24

const SAFE_VAULT_ERROR_MESSAGES: ReadonlySet<string> = new Set([
  'HTTP 200 with empty/truncated body',
  'Challenge detected',
  'WAF or captcha detected',
  'Connection timeout',
  'Rate limit exceeded',
  'Kill switch enabled',
  'Concurrent sync running',
  'Empty response from ARCA',
])

/** Resumen que escribe la sync de ARCA cuando fallan ventanas: «N/M ventanas fallaron…». */
const RESUMEN_VENTANAS_FALLIDAS = /^\d+\/\d+ ventanas fallaron/

// ============================================================================
// Lock anti-concurrencia
//
// El lock REAL es la RPC `iniciar_sync_con_lock` (advisory lock atómico con el
// INSERT del audit log — ver migración 20260515000001 + iniciarSync abajo).
// `acquireLock` se mantiene como pre-check defensivo para evitar la roundtrip
// extra a la BD cuando claramente hay un sync corriendo. NO es el lock real;
// tiene una ventana de race entre SELECT e INSERT que la RPC resuelve.
// Ref: red-team-orr100-optimizaciones-2026-05-15.md (BLOCKER B3).
// ============================================================================

/**
 * Pre-check defensivo: ¿hay otro sync 'running' del mismo source en los últimos 15 min?
 * Si lo hay → return false (caller marca audit log status='skipped' sin tocar la BD más).
 *
 * NOTA: NO es el lock real. La atomicidad la garantiza `iniciar_sync_con_lock` (RPC SQL).
 * Este chequeo evita ir a la RPC cuando hay un running reciente; ahorra una transacción.
 */
export async function acquireLock(
  client: SupabaseClient<Database>,
  source: SyncSource
): Promise<boolean> {
  const cutoff = new Date(Date.now() - RUNNING_LOCK_WINDOW_MIN * 60 * 1000).toISOString()
  const { data, error } = await loose(client)
    .from('sync_audit_log')
    .select('id')
    .eq('source', source)
    .eq('status', 'running')
    .gte('sync_started_at', cutoff)
    .limit(1)
  if (error) {
    // Si falla el query, fail-safe a NO bloquear (mejor permitir sync que perderlo todo)
    console.error('[sync] acquireLock query failed, allowing sync:', error.message)
    return true
  }
  return !data || data.length === 0
}

// ============================================================================
// Kill switch + rate limit
// ============================================================================

export async function checkKillSwitch(
  client: SupabaseClient<Database>
): Promise<boolean> {
  const { data, error } = await loose(client)
    .from('app_settings')
    .select('value')
    .eq('key', 'sync_arca_enabled')
    .single()
  if (error || !data) return true  // fail-safe: si no hay setting, asumir enabled (default true)
  return data.value === true
}

/**
 * Lee `app_settings.sync_arca_diff_cap` con fallback al default `DIFF_CAP` (50).
 *
 * Casos de uso del override:
 * - **Bootstrap inicial**: setear a 10000 una vez, correr sync para poblar el calendario,
 *   volver a 50. Sin esto, el primer sync rechaza >50 nuevos y deja calendario vacío.
 * - **RG modificatoria masiva**: ARCA publica una RG que mueve >50 vencimientos. Setear
 *   el cap temporalmente para permitir aplicar todos los cambios sin perder ninguno.
 */
export async function getDiffCap(
  client: SupabaseClient<Database>
): Promise<number> {
  const { data, error } = await loose(client)
    .from('app_settings')
    .select('value')
    .eq('key', 'sync_arca_diff_cap')
    .maybeSingle()
  if (error || !data) return DIFF_CAP
  const n = Number(data.value)
  return Number.isFinite(n) && n > 0 ? n : DIFF_CAP
}

export async function checkRateLimit(
  client: SupabaseClient<Database>,
  source: SyncSource,
  fetchedBy: string
): Promise<{ ok: boolean; reason?: string }> {
  if (fetchedBy === 'cron') return { ok: true }  // cron NO está rate-limited

  // 1. Por user / última hora
  const [limitsRes, perUserRes, globalRes] = await Promise.all([
    loose(client).from('app_settings').select('key, value').in('key', [
      'sync_arca_rate_limit_per_user_per_hour',
      'sync_arca_rate_limit_global_per_day',
    ]),
    loose(client)
      .from('sync_rate_limit_log')
      .select('id', { count: 'exact', head: true })
      .eq('source', source)
      .eq('fetched_by', fetchedBy)
      .gte('request_at', new Date(Date.now() - 60 * 60 * 1000).toISOString()),
    loose(client)
      .from('sync_rate_limit_log')
      .select('id', { count: 'exact', head: true })
      .eq('source', source)
      .gte('request_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()),
  ])

  const limits = new Map<string, number>()
  for (const row of limitsRes.data ?? []) {
    limits.set(row.key, Number(row.value))
  }
  const perUserLimit = limits.get('sync_arca_rate_limit_per_user_per_hour') ?? 1
  const globalLimit = limits.get('sync_arca_rate_limit_global_per_day') ?? 10

  if ((perUserRes.count ?? 0) >= perUserLimit) {
    return { ok: false, reason: `User exceeded ${perUserLimit}/hour` }
  }
  if ((globalRes.count ?? 0) >= globalLimit) {
    return { ok: false, reason: `Global exceeded ${globalLimit}/day` }
  }
  return { ok: true }
}

export async function registrarRateLimitHit(
  client: SupabaseClient<Database>,
  source: SyncSource,
  fetchedBy: string
): Promise<void> {
  if (fetchedBy === 'cron') return  // no cuenta
  const { error } = await loose(client).from('sync_rate_limit_log').insert({
    source,
    fetched_by: fetchedBy,
  })
  if (error) console.error('[sync] registrarRateLimitHit failed:', error.message)
}

// ============================================================================
// Audit log lifecycle
// ============================================================================

interface IniciarSyncOpts {
  source: SyncSource
  sourceUrl?: string
  fetchedBy: string
  requestIp?: string | null
  userAgent?: string | null
}

/**
 * Excepción tipada que indica que otro sync para el mismo source está corriendo
 * (lock advisory en uso). El caller debe atraparla y marcar el resultado como
 * 'skipped' en lugar de 'error'.
 *
 * Ref: docs/red-team-orr100-optimizaciones-2026-05-15.md (BLOCKER B3).
 */
export class SyncLockBusyError extends Error {
  readonly source: string
  constructor(source: string) {
    super(`Concurrent sync running for source ${source}`)
    this.name = 'SyncLockBusyError'
    this.source = source
  }
}

/**
 * Excepción tipada que indica que otra invocación de `aplicar_diff_arca` para el
 * mismo source ya tiene el advisory lock per-source (lockspace `sync_diff_*`).
 *
 * Diferencia clave con `SyncLockBusyError`:
 * - `SyncLockBusyError` ocurre EN `iniciarSync` → el audit log NO se creó.
 * - `SyncDiffLockBusyError` ocurre DESPUÉS de `iniciarSync` → el audit log YA
 *   está creado con `status='running'` y el caller debe llamar `finalizarSync`
 *   con `status='skipped'` para no dejarlo huérfano.
 *
 * Ref: PR #574 — Codex review MAJOR (race residual entre tx de iniciar_sync_con_lock
 *      y tx de aplicar_diff_arca).
 */
export class SyncDiffLockBusyError extends Error {
  readonly source: string
  constructor(source: string) {
    super(`Concurrent aplicar_diff_arca running for source ${source}`)
    this.name = 'SyncDiffLockBusyError'
    this.source = source
  }
}

/**
 * Inicia un sync llamando la RPC `iniciar_sync_con_lock`, que toma un advisory
 * lock + inserta el audit log atómicamente (misma transacción).
 *
 * - Si la RPC retorna NULL → otro sync ya tiene el lock → throw SyncLockBusyError.
 * - Si retorna un uuid → audit log creado, retornar el id.
 * - Si la RPC falla con error → throw Error con mensaje sanitizable.
 */
export async function iniciarSync(
  client: SupabaseClient<Database>,
  opts: IniciarSyncOpts
): Promise<string> {
  const { data, error } = await loose(client).rpc('iniciar_sync_con_lock', {
    p_source: opts.source,
    p_source_url: opts.sourceUrl ?? null,
    p_fetched_by: opts.fetchedBy,
    p_request_ip: opts.requestIp ?? null,
    p_user_agent: opts.userAgent ?? null,
  })
  if (error) {
    throw new Error(`Failed to acquire sync lock or insert audit log: ${error.message}`)
  }
  if (data === null || data === undefined) {
    throw new SyncLockBusyError(opts.source)
  }
  return data as string
}

interface FinalizarSyncOpts {
  auditLogId: string
  status: SyncStatus
  errorMessage?: string
  rawResponseUrl?: string
  diffsCapped?: boolean
  /**
   * Si true, el UPDATE solo se aplica si el row está todavía en `status='running'`.
   * Usado por el watchdog del handler (defense-in-depth): si el flujo normal ya
   * cerró el audit con el status real, el watchdog hace no-op en lugar de pisar.
   *
   * Por defecto false (los callers del flujo principal sobrescriben siempre, así
   * en una race contra el watchdog gana el status real del handler).
   */
  onlyIfRunning?: boolean
}

export async function finalizarSync(
  client: SupabaseClient<Database>,
  opts: FinalizarSyncOpts
): Promise<void> {
  const updates: Record<string, unknown> = {
    sync_finished_at: new Date().toISOString(),
    status: opts.status,
  }
  if (opts.errorMessage) updates.error_message = sanitizeError(opts.errorMessage)
  if (opts.rawResponseUrl) updates.raw_response_url = opts.rawResponseUrl
  if (opts.diffsCapped !== undefined) updates.diffs_capped = opts.diffsCapped

  let query = loose(client)
    .from('sync_audit_log')
    .update(updates)
    .eq('id', opts.auditLogId)
  if (opts.onlyIfRunning) {
    query = query.eq('status', 'running')
  }
  const { error } = await query
  if (error) console.error('[sync] finalizarSync failed:', error.message)
}

/**
 * Whitelist de mensajes safe para evitar leaks de stacks, paths, cookies.
 * Inspirado en patrón `sanitizeVaultError` de ORR-71.
 */
export function sanitizeError(raw: string): string {
  if (!raw) return 'Unknown error'
  const redactar = (texto: string) => texto.replace(/jsessionid=[^;]*/gi, '[REDACTED]')
  // Un mensaje que empieza con uno de la lista blanca se guarda como ese mensaje.
  for (const safe of SAFE_VAULT_ERROR_MESSAGES) {
    if (raw.startsWith(safe)) return safe
  }
  // El resumen de ventanas fallidas de la sync se guarda entero, con las cookies redactadas.
  if (RESUMEN_VENTANAS_FALLIDAS.test(raw)) return redactar(raw.split('\n')[0].slice(0, 1000))
  // Cualquier otro mensaje se guarda como error interno, con su primer renglón y las cookies redactadas.
  const firstLine = raw.split('\n')[0].slice(0, 200)
  return `Internal error (sanitized): ${redactar(firstLine)}`
}

// ============================================================================
// Escape HTML defensivo (para nombres crudos de ARCA — red-team m6)
//
// El nombre del impuesto viene del HTML scrapeado de ARCA. Si ARCA cambiara su
// markup o un atacante con control sobre el HTML inyectara tags, podríamos terminar
// renderizando `<script>` en el modal de diffs o en el cuerpo de la notif.
// Antes de poner el string en cualquier diff/notif, lo pasamos por escapeAndTruncate.
// ============================================================================

const HTML_ESCAPE_MAP: Record<string, string> = {
  '<': '&lt;',
  '>': '&gt;',
  '&': '&amp;',
  '"': '&quot;',
  "'": '&#39;',
}

/**
 * Escapa caracteres HTML peligrosos y trunca a `maxLen` con elipsis.
 *
 * Nota: el truncate cuenta caracteres de la string YA escapeada, no del input
 * crudo. Eso es intencional para garantizar el upper bound en bytes/caracteres
 * del valor final que se escribe en el audit log y la notif.
 */
export function escapeAndTruncate(input: string, maxLen = 80): string {
  const escaped = String(input).replace(/[<>&"']/g, (c) => HTML_ESCAPE_MAP[c]!)
  return escaped.length > maxLen ? escaped.slice(0, maxLen) + '…' : escaped
}

// ============================================================================
// Procesar parsed (diff + UPSERT)
// ============================================================================

interface ProcesarParsedOpts {
  auditLogId: string
  source: SyncSource
  parsed: ParsedVencimiento[]
  /**
   * Nombres CRUDOS de impuestos que el parser vio en el HTML pero NO encontró
   * en `arcaImpuestoMap`. Si llegan acá, generamos diffs sintéticos con motivo
   * `impuesto_no_mapeado` para que el modal admin los muestre y la notif a
   * superadmins los liste. Sin esto, los descartes son silenciosos.
   * Ref: docs/research-orr100-optimizaciones.md (B), red-team m6 (escape).
   */
  nombres_no_mapeados?: string[]
}

/**
 * Wrapper sobre la RPC plpgsql `aplicar_diff_arca` (Fase 2 Alt1).
 *
 * Toda la lógica de comparación + UPSERT vive ahora server-side en una sola
 * transacción atómica (ver `supabase/migrations/20260516000001_orr100_aplicar_diff_arca.sql`).
 *
 * Refs:
 *   - docs/plan-orr100-fase2-alt1.md
 *   - docs/red-team-orr100-optimizaciones-2026-05-15.md (M3, M5, M6)
 *
 * Resuelve:
 *   - M3: cuello UPDATE row-by-row + 2 SELECTs grandes desde TS. Una sola RPC.
 *   - M5: `last_synced_at` se refresca en filas `unchanged` también.
 *   - M6: consistencia transaccional contra `protected_manual`.
 *
 * Pre-procesamiento que sigue en TS:
 *   - Escape HTML de `nombres_no_mapeados` (mantiene la lógica de escape en un
 *     solo lugar y evita portarla a plpgsql). La RPC los recibe ya escapados y
 *     los wrap-ea como diffs sintéticos sin re-procesar.
 *   - Lectura del cap configurable (`app_settings.sync_arca_diff_cap`).
 */
export async function procesarParsed(
  client: SupabaseClient<Database>,
  opts: ProcesarParsedOpts
): Promise<ProcesarParsedResult> {
  const { auditLogId, source, parsed, nombres_no_mapeados } = opts

  // 1. Pre-procesar nombres_no_mapeados: escape HTML + truncate (defensa contra
  //    inyecciones — red-team M6). La RPC los consume ya saneados.
  const nombresEscapados = (nombres_no_mapeados ?? []).map((n) => escapeAndTruncate(n))

  // 2. Cap configurable (app_settings.sync_arca_diff_cap, default DIFF_CAP=50).
  //    Sigue acá porque es app_settings, no parámetro de la RPC.
  const cap = await getDiffCap(client)

  // 3. Una sola round-trip a Postgres: la RPC dedupea, classifica, aplica
  //    INSERT/UPDATE batches, refresca last_synced_at y persiste diffs+counters
  //    en sync_audit_log dentro de una única transacción.
  const { data, error } = await loose(client).rpc('aplicar_diff_arca', {
    p_audit_log_id: auditLogId,
    p_source: source,
    p_parsed: parsed,
    p_nombres_no_mapeados: nombresEscapados,
    p_diff_cap: cap,
  })

  if (error) {
    throw new Error(`aplicar_diff_arca failed: ${error.message}`)
  }
  // RPC retorna NULL específicamente cuando el advisory lock per-source
  // (lockspace sync_diff_<source>) está ocupado por otra invocación concurrente.
  // Distinto de `error` (que indica fallo real de la RPC). El caller debe
  // limpiar el audit log creado por iniciarSync con finalizarSync('skipped').
  if (data === null || data === undefined) {
    throw new SyncDiffLockBusyError(source)
  }

  return data as ProcesarParsedResult
}

// ============================================================================
// Suspicious fetch detection
// ============================================================================

export function isFetchSuspicious(recordsFetched: number): boolean {
  return recordsFetched < HISTORICAL_MIN_FETCHED * SUSPICIOUS_FETCH_RATIO
}

// ============================================================================
// Read APIs (UI admin)
// ============================================================================

interface ListarRecientesOpts {
  source?: SyncSource
  status?: SyncStatus
  limit?: number
}

export async function listarRecientes(
  client: SupabaseClient<Database>,
  opts: ListarRecientesOpts = {}
): Promise<SyncAuditLog[]> {
  let query = loose(client)
    .from('sync_audit_log')
    .select('*')
    .order('sync_started_at', { ascending: false })
    .limit(opts.limit ?? 30)
  if (opts.source) query = query.eq('source', opts.source)
  if (opts.status) query = query.eq('status', opts.status)
  const { data, error } = await query
  if (error) throw error
  return (data ?? []) as unknown as SyncAuditLog[]
}

/**
 * Estados de una sync que trajo fechas de la fuente. Una sync `partial` también aplica lo que trajo: solo le faltó
 * alguna ventana o alguna fila.
 */
export const ESTADOS_SYNC_CON_DATOS = ['success', 'partial'] as const

/** Devuelve la última sync de `source` que trajo filas, exitosa o parcial. */
export async function obtenerUltimaSync(
  client: SupabaseClient<Database>,
  source: SyncSource
): Promise<SyncAuditLog | null> {
  const { data, error } = await loose(client)
    .from('sync_audit_log')
    .select('*')
    .eq('source', source)
    .in('status', ESTADOS_SYNC_CON_DATOS)
    .gt('records_fetched', 0)
    .order('sync_finished_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return (data ?? null) as unknown as SyncAuditLog | null
}

// ============================================================================
// Marcar revisado (acción admin desde UI)
// ============================================================================

export async function marcarRevisado(
  client: SupabaseClient<Database>,
  auditLogId: string,
  revisadoPor: string
): Promise<void> {
  const { error } = await loose(client)
    .from('sync_audit_log')
    .update({
      revisado_at: new Date().toISOString(),
      revisado_por: revisadoPor,
    })
    .eq('id', auditLogId)
  if (error) throw error
}

// ============================================================================
// Notificaciones (post-sync, fuera de la "transacción virtual")
// ============================================================================

interface DispararNotificacionesOpts {
  auditLogId: string
  source: SyncSource
  diffs: SyncDiff[]
  recordsFetched: number
  status: SyncStatus
  /**
   * Nombres CRUDOS de impuestos no mapeados (sin escape HTML). Usados para
   * construir el `mensaje` de la notif IMPUESTO_NO_MAPEADO. Necesarios para
   * evitar doble-escape: los diffs ya tienen `valor_nuevo` HTML-escapeado y
   * se renderizan como text content de React → mostrarían `&lt;script&gt;`
   * en lugar del char real. Acá usamos los originales y truncamos sin escape.
   * Si no se pasa, se cae al `valor_nuevo` escapado (compat hacia atrás).
   */
  nombres_no_mapeados?: string[]
}

/**
 * Dispara notif a superadmins (DIFF_DETECTADO o ERROR) y a contadores responsables
 * de cada cliente afectado (CLIENTE_AFECTADO).
 *
 * Anti-spam: dedup 24h por tipo+destinatario.
 * PII: emails de responsables calculados en runtime, no persistidos en audit log.
 *
 * Best-effort: cada fase con try/catch propio, no rompe el sync si falla notif.
 */
export async function dispararNotificaciones(
  client: SupabaseClient<Database>,
  opts: DispararNotificacionesOpts
): Promise<void> {
  const { PLANTILLAS_NOTIFICACION } = await import('../types/notificaciones')
  const { notificacionesService } = await import('./notificacionesService')

  // ── Cargar destinatarios superadmin (whitelist en app_settings)
  let superadminEmails: string[] = []
  try {
    const { data } = await loose(client)
      .from('app_settings')
      .select('value')
      .eq('key', 'sync_arca_superadmin_emails')
      .single()
    if (data?.value && Array.isArray(data.value)) {
      superadminEmails = (data.value as unknown[]).filter((v): v is string => typeof v === 'string')
    }
  } catch (err) {
    console.error('[sync] failed to load superadmin emails:', err instanceof Error ? err.message : err)
  }

  // ── Helper: dedup notif (skip si existe una similar en últimas 24h)
  const dedupCutoff = new Date(Date.now() - NOTIF_DEDUP_HOURS * 60 * 60 * 1000).toISOString()
  async function emailsConDedup(emails: string[], tipo: string, titulo: string): Promise<string[]> {
    if (emails.length === 0) return []
    try {
      const { data } = await client
        .from('notificaciones')
        .select('user_email')
        .in('user_email', emails)
        .eq('tipo', tipo as never)
        .eq('titulo', titulo)
        .gte('created_at', dedupCutoff)
      const dupSet = new Set((data ?? []).map((r) => r.user_email))
      return emails.filter((e) => !dupSet.has(e))
    } catch {
      return emails // fail-open: no perder notif por bug de dedup
    }
  }

  // ── ERROR: notif a superadmins
  if (opts.status === 'error') {
    try {
      const tpl = PLANTILLAS_NOTIFICACION.syncVencimientosError(opts.source, opts.status)
      const destinatarios = await emailsConDedup(superadminEmails, tpl.tipo, tpl.titulo)
      if (destinatarios.length > 0) {
        await notificacionesService.crearParaMultiples(client, destinatarios, {
          tipo: tpl.tipo,
          titulo: tpl.titulo,
          mensaje: tpl.mensaje,
          enlace: tpl.enlace,
        })
      }
    } catch (err) {
      console.error('[sync] notif ERROR failed:', err instanceof Error ? err.message : err)
    }
    return // no continuar con DIFF/CLIENTE notif si el sync falló
  }

  // ── DIFF_DETECTADO: notif a superadmins si hay diffs reales.
  // Excluimos:
  //   - manual_override_skipped: no hubo cambio efectivo en BD (la fila quedó protegida).
  //   - impuesto_no_mapeado: tiene su propia notif agrupada más abajo.
  // El "top impuesto" requeriría un JOIN al catálogo para mostrar nombre legible,
  // y el detalle ya está visible en /admin/sync-vencimientos. La notif pasa
  // null/0 que el template tolera.
  const diffsReales = opts.diffs.filter(
    (d) => d.motivo !== 'manual_override_skipped' && d.motivo !== 'impuesto_no_mapeado',
  )
  if (diffsReales.length > 0) {
    try {
      const tpl = PLANTILLAS_NOTIFICACION.syncVencimientosDiffDetectado(
        opts.source,
        diffsReales.length,
        null,
        0,
      )
      const destinatarios = await emailsConDedup(superadminEmails, tpl.tipo, tpl.titulo)
      if (destinatarios.length > 0) {
        await notificacionesService.crearParaMultiples(client, destinatarios, {
          tipo: tpl.tipo,
          titulo: tpl.titulo,
          mensaje: tpl.mensaje,
          enlace: tpl.enlace,
        })
      }
    } catch (err) {
      console.error('[sync] notif DIFF failed:', err instanceof Error ? err.message : err)
    }
  }

  // ── IMPUESTO_NO_MAPEADO: notif agrupada a superadmins.
  // Título FIJO ("Impuesto nuevo de ARCA detectado") para que el dedup 24h por
  // (tipo + titulo + user_email) funcione: si ARCA mantiene el impuesto sin mapeo
  // varios días seguidos, no spameamos a los superadmins una vez por día. La lista
  // de nombres va en el cuerpo (mensaje), no en el título.
  // Ref: red-team M8 (dedup necesita título estable).
  const diffsImpuestoNoMapeado = opts.diffs.filter((d) => d.motivo === 'impuesto_no_mapeado')
  if (diffsImpuestoNoMapeado.length > 0) {
    try {
      // Nombres para el mensaje: priorizar los CRUDOS (no escapeados) que vienen
      // del handler. NotificacionItem.tsx renderiza `mensaje` como text content
      // de React, por lo que escaparlo acá produciría doble-escape visible
      // (`&lt;script&gt;` en lugar de `<script>`). El escape sigue aplicado en
      // los diffs persistidos en audit log (defense in depth contra otros
      // consumidores que sí podrían usar dangerouslySetInnerHTML).
      const TRUNCATE_LEN = 80
      const truncate = (s: string) => (s.length > TRUNCATE_LEN ? s.slice(0, TRUNCATE_LEN) + '…' : s)
      const nombres =
        opts.nombres_no_mapeados && opts.nombres_no_mapeados.length > 0
          ? opts.nombres_no_mapeados.map(truncate).join(', ')
          : diffsImpuestoNoMapeado.map((d) => d.valor_nuevo ?? '?').join(', ')
      const titulo = 'Impuesto nuevo de ARCA detectado'  // FIJO — no interpolar nombres acá.
      const mensaje = `Detectados ${diffsImpuestoNoMapeado.length} impuesto(s) en ${opts.source} sin mapeo en Menta: ${nombres}. Revisar src/core/services/arcaImpuestoMap.ts.`
      const tipo = 'alerta' as const
      const destinatarios = await emailsConDedup(superadminEmails, tipo, titulo)
      if (destinatarios.length > 0) {
        await notificacionesService.crearParaMultiples(client, destinatarios, {
          tipo,
          titulo,
          mensaje,
          enlace: '/admin/sync-vencimientos',
        })
      }
    } catch (err) {
      console.error('[sync] notif IMPUESTO_NO_MAPEADO failed:', err instanceof Error ? err.message : err)
    }
  }

  // ── CLIENTE_AFECTADO: notif a responsables de cada cliente con vencimiento afectado
  // Los diffs con calendario_afip_id != null + motivo != manual_skipped pueden tener
  // materializaciones downstream en vencimientos_fiscales.
  const calendarioIds = diffsReales
    .filter((d) => d.calendario_afip_id != null && d.motivo === 'cambio_fecha')
    .map((d) => d.calendario_afip_id as string)

  if (calendarioIds.length > 0) {
    try {
      // 1. Map calendarioId → diff (para fecha vieja/nueva) — Codex P2 fix
      const diffByCalendario = new Map(
        diffsReales
          .filter((d) => d.calendario_afip_id && d.motivo === 'cambio_fecha')
          .map((d) => [d.calendario_afip_id as string, d])
      )

      // 2. Encontrar vencimientos materializados → cliente_id + impuesto_id + periodo
      const { data: vf } = await loose(client)
        .from('vencimientos_fiscales')
        .select('cliente_id, calendario_afip_id, impuesto_id, periodo')
        .in('calendario_afip_id', calendarioIds)
      const vfRows = (vf ?? []) as Array<{
        cliente_id: string
        calendario_afip_id: string
        impuesto_id: string
        periodo: string
      }>
      const clienteIds = Array.from(new Set(vfRows.map((r) => r.cliente_id).filter(Boolean)))

      if (clienteIds.length > 0) {
        // 3. Resolver responsables + datos del cliente + catálogo de impuestos
        const impuestoIds = Array.from(new Set(vfRows.map((r) => r.impuesto_id).filter(Boolean)))
        const [{ data: responsables }, { data: clientes }, { data: catalogo }] = await Promise.all([
          loose(client)
            .from('cliente_responsables')
            .select('cliente_id, usuario_id, usuarios_brainhub(email)')
            .in('cliente_id', clienteIds),
          loose(client)
            .from('clientes')
            .select('id, nombre')
            .in('id', clienteIds),
          loose(client)
            .from('catalogo_impuestos')
            .select('id, nombre, nombre_corto')
            .in('id', impuestoIds),
        ])

        const clienteNombreMap = new Map<string, string>(
          (clientes ?? []).map((c) => [c.id as string, (c.nombre as string) ?? 'Cliente'])
        )
        const impuestoNombreMap = new Map<string, string>(
          (catalogo ?? []).map((c) => [c.id as string, (c.nombre_corto ?? c.nombre ?? opts.source) as string])
        )

        // 4. Por cada cliente, elegir el primer vf afectado para enriquecer el mensaje.
        // Si tiene múltiples, la notif representa "uno de ellos cambió"; el contador
        // va al cliente y ve la lista completa en su detail page.
        const detalleByCliente = new Map<string, { impuesto: string; periodo: string; fechaVieja: string; fechaNueva: string }>()
        for (const r of vfRows) {
          if (detalleByCliente.has(r.cliente_id)) continue
          const diff = diffByCalendario.get(r.calendario_afip_id)
          if (!diff) continue
          detalleByCliente.set(r.cliente_id, {
            impuesto: impuestoNombreMap.get(r.impuesto_id) ?? opts.source,
            periodo: r.periodo,
            fechaVieja: diff.valor_viejo ?? '',
            fechaNueva: diff.valor_nuevo ?? '',
          })
        }

        // 5. Dedup contra notificaciones ya enviadas en últimas 24h
        const tpl0 = PLANTILLAS_NOTIFICACION.syncVencimientosClienteAfectado('', '', '', '', '', '')
        const allResponsableEmails: string[] = Array.from(
          new Set(
            (responsables ?? [])
              .map((r) => {
                const u = (r as { usuarios_brainhub?: { email?: string } | { email?: string }[] }).usuarios_brainhub
                if (!u) return undefined
                if (Array.isArray(u)) return u[0]?.email
                return u.email
              })
              .filter((e): e is string => typeof e === 'string')
          )
        )
        const yaNotificados = new Set<string>()
        try {
          const { data } = await client
            .from('notificaciones')
            .select('user_email')
            .in('user_email', allResponsableEmails)
            .eq('tipo', tpl0.tipo as never)
            .gte('created_at', dedupCutoff)
          for (const r of data ?? []) yaNotificados.add(r.user_email)
        } catch {
          // fail-open
        }

        // 6. Construir inserts con datos reales (no strings vacíos)
        const inserts: Array<{ user_email: string; tipo: typeof tpl0.tipo; titulo: string; mensaje: string; enlace: string | null }> = []
        for (const resp of responsables ?? []) {
          const u = (resp as { usuarios_brainhub?: { email?: string } | { email?: string }[] }).usuarios_brainhub
          const email = Array.isArray(u) ? u[0]?.email : u?.email
          if (!email || yaNotificados.has(email)) continue
          const clienteId = resp.cliente_id as string
          const detalle = detalleByCliente.get(clienteId)
          if (!detalle) continue // sin diff cambio_fecha mapeado a este cliente: skip notif
          const clienteNombre = clienteNombreMap.get(clienteId) ?? 'Cliente'
          const tpl = PLANTILLAS_NOTIFICACION.syncVencimientosClienteAfectado(
            clienteNombre,
            clienteId,
            detalle.impuesto,
            detalle.periodo,
            detalle.fechaVieja,
            detalle.fechaNueva,
          )
          inserts.push({
            user_email: email,
            tipo: tpl.tipo,
            titulo: tpl.titulo,
            mensaje: tpl.mensaje,
            enlace: tpl.enlace,
          })
          yaNotificados.add(email) // dedup intra-loop
        }
        if (inserts.length > 0) {
          // Insert masivo
          await loose(client).from('notificaciones').insert(
            inserts.map((i) => ({ ...i, leida: false }))
          )
        }
      }
    } catch (err) {
      console.error('[sync] notif CLIENTE_AFECTADO failed:', err instanceof Error ? err.message : err)
    }
  }
}
